/**
 * Maps nationality strings to ISO 3166-1 alpha-2 codes (lowercase).
 * Includes both adjective forms ("English") and country-name forms ("England")
 * so we handle whatever format the backend stores.
 *
 * SVGs are self-hosted in public/flags/4x3/ (copied from the flag-icons npm package).
 * Sub-national codes (gb-eng, gb-sct, gb-wls, gb-nir) are included.
 */
const nationalityFlags: Record<string, string> = {
  // British nations (sub-national)
  English: "gb-eng",  England: "gb-eng",
  Scottish: "gb-sct", Scotland: "gb-sct",
  Welsh: "gb-wls",    Wales: "gb-wls",
  "Northern Irish": "gb-nir", "Northern Ireland": "gb-nir",
  British: "gb",      "United Kingdom": "gb",

  // Europe
  Albanian: "al",     Albania: "al",
  Andorran: "ad",     Andorra: "ad",
  Austrian: "at",     Austria: "at",
  Azerbaijani: "az",  Azerbaijan: "az",
  Belarusian: "by",   Belarus: "by",
  Belgian: "be",      Belgium: "be",
  Bosnian: "ba",      "Bosnia and Herzegovina": "ba", "Bosnian-Herzegovinian": "ba",
  Bulgarian: "bg",    Bulgaria: "bg",
  Croatian: "hr",     Croatia: "hr",
  Cypriot: "cy",      Cyprus: "cy",
  Czech: "cz",        "Czech Republic": "cz", Czechia: "cz",
  Danish: "dk",       Denmark: "dk",
  Dutch: "nl",        Netherlands: "nl", "The Netherlands": "nl",
  Estonian: "ee",     Estonia: "ee",
  Faroese: "fo",      "Faroe Islands": "fo",
  Finnish: "fi",      Finland: "fi",
  French: "fr",       France: "fr",
  Georgian: "ge",     Georgia: "ge",
  German: "de",       Germany: "de",
  Greek: "gr",        Greece: "gr",
  Hungarian: "hu",    Hungary: "hu",
  Icelandic: "is",    Iceland: "is",
  Irish: "ie",        Ireland: "ie", "Republic of Ireland": "ie",
  Italian: "it",      Italy: "it",
  Kosovar: "xk",      Kosovo: "xk",
  Latvian: "lv",      Latvia: "lv",
  Lithuanian: "lt",   Lithuania: "lt",
  Luxembourgish: "lu", Luxembourg: "lu",
  Macedonian: "mk",   "North Macedonia": "mk",
  Maltese: "mt",      Malta: "mt",
  Moldovan: "md",     Moldova: "md",
  Montenegrin: "me",  Montenegro: "me",
  Norwegian: "no",    Norway: "no",
  Polish: "pl",       Poland: "pl",
  Portuguese: "pt",   Portugal: "pt",
  Romanian: "ro",     Romania: "ro",
  Russian: "ru",      Russia: "ru",
  Serbian: "rs",      Serbia: "rs",
  Slovak: "sk",       Slovakia: "sk",
  Slovenian: "si",    Slovenia: "si",
  Spanish: "es",      Spain: "es",
  Swedish: "se",      Sweden: "se",
  Swiss: "ch",        Switzerland: "ch",
  Turkish: "tr",      Turkey: "tr",   Türkiye: "tr",
  Ukrainian: "ua",    Ukraine: "ua",

  // Americas
  American: "us",     "United States": "us", USA: "us", "United States of America": "us",
  Argentine: "ar",    Argentina: "ar",
  Barbadian: "bb",    Barbados: "bb",
  Bolivian: "bo",     Bolivia: "bo",
  Brazilian: "br",    Brazil: "br",
  Canadian: "ca",     Canada: "ca",
  Chilean: "cl",      Chile: "cl",
  Colombian: "co",    Colombia: "co",
  "Costa Rican": "cr", "Costa Rica": "cr",
  Cuban: "cu",        Cuba: "cu",
  Curaçaoan: "cw",    Curaçao: "cw",
  Ecuadorian: "ec",   Ecuador: "ec",
  Guatemalan: "gt",   Guatemala: "gt",
  Haitian: "ht",      Haiti: "ht",
  Honduran: "hn",     Honduras: "hn",
  Jamaican: "jm",     Jamaica: "jm",
  Mexican: "mx",      Mexico: "mx",
  Panamanian: "pa",   Panama: "pa",
  Paraguayan: "py",   Paraguay: "py",
  Peruvian: "pe",     Peru: "pe",
  Trinidadian: "tt",  "Trinidad and Tobago": "tt", "Trinidad and Tobagonian": "tt",
  Uruguayan: "uy",    Uruguay: "uy",
  Venezuelan: "ve",   Venezuela: "ve",

  // Africa
  Algerian: "dz",         Algeria: "dz",
  Angolan: "ao",          Angola: "ao",
  "Burkina Fasan": "bf",  "Burkina Faso": "bf",
  Burundian: "bi",        Burundi: "bi",
  Cameroonian: "cm",      Cameroon: "cm",
  "Cape Verdean": "cv",   "Cape Verde": "cv",   "Cabo Verde": "cv",
  Chadian: "td",          Chad: "td",
  Congolese: "cg",        Congo: "cg",
  "DR Congolese": "cd",   "DR Congo": "cd", "Democratic Republic of the Congo": "cd",
  Egyptian: "eg",         Egypt: "eg",
  "Equatorial Guinean": "gq", "Equatorial Guinea": "gq",
  Ethiopian: "et",        Ethiopia: "et",
  Gabonese: "ga",         Gabon: "ga",
  Gambian: "gm",          Gambia: "gm",
  Ghanaian: "gh",         Ghana: "gh",
  Guinean: "gn",          Guinea: "gn",
  "Guinea-Bissauan": "gw", "Guinea-Bissau": "gw",
  Ivorian: "ci",          "Ivory Coast": "ci", "Côte d'Ivoire": "ci",
  Kenyan: "ke",           Kenya: "ke",
  Libyan: "ly",           Libya: "ly",
  Madagascan: "mg",       Madagascar: "mg",
  Malian: "ml",           Mali: "ml",
  Mauritanian: "mr",      Mauritania: "mr",
  Moroccan: "ma",         Morocco: "ma",
  Mozambican: "mz",       Mozambique: "mz",
  Namibian: "na",         Namibia: "na",
  Nigerian: "ng",         Nigeria: "ng",
  Rwandan: "rw",          Rwanda: "rw",
  Senegalese: "sn",       Senegal: "sn",
  "Sierra Leonean": "sl", "Sierra Leone": "sl",
  Somalian: "so",         Somalia: "so",
  "South African": "za",  "South Africa": "za",
  "South Sudanese": "ss", "South Sudan": "ss",
  Sudanese: "sd",         Sudan: "sd",
  Tanzanian: "tz",        Tanzania: "tz",
  Togolese: "tg",         Togo: "tg",
  Tunisian: "tn",         Tunisia: "tn",
  Ugandan: "ug",          Uganda: "ug",
  Zambian: "zm",          Zambia: "zm",
  Zimbabwean: "zw",       Zimbabwe: "zw",

  // Asia & Oceania
  Afghan: "af",               Afghanistan: "af",
  Australian: "au",           Australia: "au",
  Bahraini: "bh",             Bahrain: "bh",
  Chinese: "cn",              China: "cn",
  "Hong Konger": "hk",        "Hong Kong": "hk",
  Indian: "in",               India: "in",
  Indonesian: "id",           Indonesia: "id",
  Iranian: "ir",              Iran: "ir",
  Iraqi: "iq",                Iraq: "iq",
  Israeli: "il",              Israel: "il",
  Japanese: "jp",             Japan: "jp",
  Jordanian: "jo",            Jordan: "jo",
  Korean: "kr",               "South Korean": "kr", "South Korea": "kr",
  Kuwaiti: "kw",              Kuwait: "kw",
  Lebanese: "lb",             Lebanon: "lb",
  Malaysian: "my",            Malaysia: "my",
  "New Zealander": "nz",      "New Zealand": "nz",
  Omani: "om",                Oman: "om",
  Pakistani: "pk",            Pakistan: "pk",
  Palestinian: "ps",          Palestine: "ps",
  Philippine: "ph",           Filipino: "ph", Philippines: "ph",
  Qatari: "qa",               Qatar: "qa",
  "Saudi Arabian": "sa",      "Saudi Arabia": "sa",
  Syrian: "sy",               Syria: "sy",
  Thai: "th",                 Thailand: "th",
  "United Arab Emirati": "ae", "United Arab Emirates": "ae", UAE: "ae",
  Uzbek: "uz",                Uzbekistan: "uz",
  Vietnamese: "vn",           Vietnam: "vn",
};

export function getFlagUrl(nationality: string): string | null {
  if (!nationality) return null;
  const code = nationalityFlags[nationality];
  if (!code) return null;
  return `/flags/4x3/${code}.svg`;
}
